import React, {useCallback, useState} from 'react';
import {useLocation} from "react-router-dom";
import {
  Column,
  CellProps
} from 'react-table';
import {ListTableWithSearchBox, SearchParams} from "components/pages/common/table/ListTableWithSearchBox";
import useAppRouter from "../../hooks/useAppRouter";
import {labelSetApi} from "../../apis/admin/menu/labelSetApi";
import {formatOmittedDateTime} from "../../libs/utils/formatDate";
import {logError} from "../../libs/utils/logError";

interface LabelSet {
  id: number
  name: string
  content: string
  createdAt: string
  updatedAt: string
}

interface DataType extends LabelSet {
  operation?: string
}

export const LabelSetListPage: React.FC = () => {
  const [labelSetList, setLabelSetList] = useState<DataType[]>([]);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [totalCount, setTotalCount] = React.useState<number>(10);
  const { goto } = useAppRouter();
  const location = useLocation();

  const sortFunc = async (params: SearchParams) => {
    setAbortController(new AbortController());
    try
    {
      const offset = (params.currentPage - 1) * params.size;
      const response = await labelSetApi.index(params.keyword, params.size, offset, params.sort, { abortSignal: abortController?.signal });
      setAbortController(undefined);
      setLabelSetList(response.labelSetList);
      setTotalCount(response.totalHits);
    } catch(e) {
      await logError(
        location.pathname,
        'ラベルセット一覧の取得に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  }

  const initialSort = [
    'createdAt:desc'
  ];

  const handleCreate = async () => {
    goto('labelSetCreate');
  }

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ formatOmittedDateTime(value) }</div>
  ), []);

  const OperationCell = useCallback(({ row }: CellProps<DataType>) => (
    <button
      className="btn-primary focus:opacity-50"
      type='button'
      onClick={() => {
        goto('labelSetEdit', undefined, { id: row.original.id });
      }}
    >
      編 集
    </button>
  ), [goto]);

  const columns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'name',
        className: 'select-none',
      },
      {
        Header: '説明',
        accessor: 'content',
        disableSortBy: true,
      },
      {
        Header: '作成日時',
        accessor: 'createdAt',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
        disableSortBy: true,
      },
    ],
    [OperationCell, DateTimeCell]
  );

  return (
    <>
      <div className="bg-sub min-h-full px-8">
        <div className="flex items-center py-6">
          <div className="text-xl text-primary font-semibold">ラベルセット</div>
          <div className="ml-auto">
            <button type="button"
                    className="btn-primary"
                    onClick={() => {
                      handleCreate();
                    }}
            >
              追 加
            </button>
          </div>
        </div>
        <ListTableWithSearchBox
          {...{
            tableData: labelSetList,
            columns,
            initialSort,
            totalCount,
            sortFunc,
          }}
        />
      </div>
    </>
  );
};